import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "@privacy/translations";
import { IconButton, Menu, Box, Stack, Button, MenuItem, Typography, Tooltip, Divider } from "@mui/material";
import { CircularProgress } from "@privacy/ui";
import { useForm } from "react-hook-form";
import { TrashCanIcon, RiskIcon1, MoreHorizontalIcon } from "../../shared/icons";
import {
  dpiaRiskAssessmentsQueryOptions,
  deleteDpiaRiskAssessmentMutationOptions,
  createDpiaRiskAssessmentMutationOptions,
  dpiaRiskAssessmentMatrixByIdQueryOptions,
  useUpdateDpiaRiskAssessmentById,
} from "../../api/dpia";
import { DynamicForm } from "../../dynamicForm";
import { PageSpinner } from "../../shared/Page";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import * as FormDialog from "../../shared/FormDialog";
import { ErrorMessage } from "../../dynamicForm/inlineForm/ErrorMessage";
import type { DpiaRiskAssessmentPayload, DpiaRiskAssessmentsResponse } from "../../api/dpia.types";
import { visuallyHidden } from "@mui/utils";
import { useMutation, useQuery } from "@tanstack/react-query";

export const RiskAssessment: FC<{
  dpiaId: string;
  updateRiskAssessmentMutation: ReturnType<typeof useUpdateDpiaRiskAssessmentById>;
  isReadOnly: boolean;
}> = ({ dpiaId, updateRiskAssessmentMutation, isReadOnly }) => {
  const { t } = useTranslation();
  const [isAddRiskDialogOpen, setIsAddRiskDialogOpen] = useState(false);
  const riskAssessmentsQuery = useQuery(dpiaRiskAssessmentsQueryOptions(dpiaId));

  return (
    <Box sx={{ width: "100%" }}>
      {(() => {
        if (!riskAssessmentsQuery.data) return <PageSpinner />;

        return (
          <Stack sx={{ gap: 4 }}>
            <Box sx={{ width: "100%", containerType: "inline-size" }}>
              <Stack
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 2,
                  ...(riskAssessmentsQuery.data.riskAssessments.length > 0
                    ? { [SMALLER_THAN_RISK_CARD_BREAKPOINT_QUERY]: { gridTemplateColumns: "1fr", gap: 6 } }
                    : {}),
                }}
              >
                <Typography
                  sx={{
                    fontSize: theme => theme.typography.pxToRem(14),
                    color: theme => theme.palette.text.tertiary,
                    lineHeight: 1.5,
                  }}
                >
                  {t("Dpia.Risks.Description")}
                </Typography>
                {riskAssessmentsQuery.data.riskAssessments.length > 0 && (
                  <Stack sx={{ position: "sticky", top: 0 }}>
                    <RiskMatrix dpiaId={dpiaId} />
                  </Stack>
                )}
              </Stack>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 6 }}>
                <Button onClick={() => setIsAddRiskDialogOpen(true)} disabled={isReadOnly}>
                  {t("Dpia.AddRisk.Button")}
                </Button>
              </Box>
            </Box>
            <Stack sx={{ gap: 4 }}>
              {riskAssessmentsQuery.data.riskAssessments.map(risk => (
                <DynamicForm.Provider
                  key={risk.id}
                  onSave={answer => updateRiskAssessmentMutation.mutateAsync({ riskId: risk.id, answers: [answer] })}
                  answers={risk.answers}
                  isReadOnly={isReadOnly}
                  isSaveError={updateRiskAssessmentMutation.isError}
                  isSaving={updateRiskAssessmentMutation.isPending}
                >
                  <RiskCard risk={risk} form={riskAssessmentsQuery.data} isReadOnly={isReadOnly} dpiaId={dpiaId} />
                </DynamicForm.Provider>
              ))}
              {riskAssessmentsQuery.data.riskAssessments.length === 0 && <RisksEmptyState />}
            </Stack>
          </Stack>
        );
      })()}
      {isAddRiskDialogOpen && (
        <CreateRiskAssessmentDialog dpiaId={dpiaId} onClose={() => setIsAddRiskDialogOpen(false)} />
      )}
    </Box>
  );
};

const NAME_LABEL_PREFIX = (riskId: string) => `name-${riskId}`;
const DESCRIPTION_LABEL_PREFIX = (riskId: string) => `description-${riskId}`;

const RiskTitleAndDescription: FC<{
  dpiaId: string;
  risk: DpiaRiskAssessmentsResponse["riskAssessments"][0];
  isReadOnly: boolean;
}> = ({ dpiaId, risk, isReadOnly }) => {
  const { t } = useTranslation();
  const riskDescriptionAndTitleMutation = useMutation(createDpiaRiskAssessmentMutationOptions());
  const { name, description, id: riskId } = risk;

  const form = useForm<Record<string, string>>({
    defaultValues: { [`name-${riskId}`]: name, [`description-${riskId}`]: description },
  });

  return (
    <Stack sx={{ mt: 0.5, gap: 0.5 }}>
      <Box component="label" sx={visuallyHidden} htmlFor={NAME_LABEL_PREFIX(riskId)}>
        {t("Dpia.Risks.RiskCard.NameLabel")}
      </Box>
      <DynamicForm.StyledInlineTextInput
        onSave={updatedName => {
          return riskDescriptionAndTitleMutation.mutateAsync(
            { dpiaId, id: riskId, name: updatedName ?? "", description },
            {
              onSuccess: () => {
                form.reset({ [`name-${riskId}`]: updatedName, [`description-${riskId}`]: description });
              },
            }
          );
        }}
        validation={{ validator: value => value.trim().length > 0 }}
        form={form}
        numberOfRows={1}
        id={NAME_LABEL_PREFIX(riskId)}
        isReadOnly={isReadOnly}
        renderErrorMessage={({ isSaving, isValidationError, isSaveError }) => (
          <>
            {!isSaving && isSaveError && <DynamicForm.ErrorMessage />}
            {isValidationError && <DynamicForm.ErrorMessage errorMessage={t("Input.Validation.RequiredName")} />}
          </>
        )}
        inputSx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(16), height: "auto" }}
      />
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <Box component="label" sx={visuallyHidden} htmlFor={DESCRIPTION_LABEL_PREFIX(riskId)}>
          {t("Dpia.Risks.RiskCard.DescriptionLabel")}
        </Box>
        <DynamicForm.StyledInlineTextInput
          onSave={updatedDescription => {
            return riskDescriptionAndTitleMutation.mutateAsync(
              {
                id: risk.id,
                dpiaId,
                name: risk.name,
                description: updatedDescription,
              },
              {
                onSuccess: () =>
                  form.reset({ [`name-${riskId}`]: name, [`description-${riskId}`]: updatedDescription }),
              }
            );
          }}
          validation={{ validator: value => value.trim().length > 0 }}
          form={form}
          numberOfRows={3}
          id={DESCRIPTION_LABEL_PREFIX(riskId)}
          isReadOnly={isReadOnly}
          renderErrorMessage={({ isSaving, isValidationError, isSaveError }) => (
            <>
              {!isSaving && isSaveError && <DynamicForm.ErrorMessage />}
              {isValidationError && (
                <DynamicForm.ErrorMessage errorMessage={t("OptionCategoryGrid.DescriptionRequired")} />
              )}
            </>
          )}
          inputSx={{ color: theme => theme.palette.text.tertiary }}
        />
      </Stack>
      {riskDescriptionAndTitleMutation.isError && (
        <ErrorMessage variant="error" errorMessage={t("Dpia.Risks.SaveError")} />
      )}
      <Box sx={{ pl: 2, mt: 2 }}>
        <RiskLevelLabel
          label={risk.riskLevel.riskLevelLabel}
          backgroundColor={risk.riskLevel.riskLevelBackgroundColor}
          color={risk.riskLevel.riskLevelColor}
        />
      </Box>
    </Stack>
  );
};

const RiskAssessmentCardMenu: FC<{ riskAssessment: DpiaRiskAssessmentsResponse["riskAssessments"][0] }> = ({
  riskAssessment: risk,
}) => {
  const { t } = useTranslation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
  const deleteRiskAssessment = useMutation(deleteDpiaRiskAssessmentMutationOptions());

  const handleDelete = (riskId: string) =>
    deleteRiskAssessment.mutateAsync(riskId, { onSuccess: () => setIsDeleteDialogOpen(false) });

  const handleClose = () => {
    setIsDeleteDialogOpen(false);
    setButtonRef(null);
  };
  const isMenuOpen = Boolean(buttonRef);
  return (
    <>
      <IconButton
        aria-label={t("GridDrawer.Menu.ButtonLabel")}
        id={`menu-button-${risk.id}`}
        aria-controls={isMenuOpen ? "risk-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? "true" : undefined}
        title={t("GridDrawer.Menu.ButtonLabel")}
        color="primary"
        onClick={e => setButtonRef(e.currentTarget)}
      >
        <MoreHorizontalIcon size={16} />
      </IconButton>
      <Menu
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        id="grid-drawer-menu"
        anchorEl={buttonRef}
        open={isMenuOpen}
        onClose={handleClose}
        slotProps={{
          list: { "aria-labelledby": "grid-drawer-menu-button" },
        }}
      >
        <MenuItem onClick={() => setIsDeleteDialogOpen(true)} disabled={false} data-testid="deletebtn">
          <TrashCanIcon color="currentColor" />
          {t("Dpia.RiskMenu.Delete")}
        </MenuItem>
      </Menu>
      {isDeleteDialogOpen && (
        <ConfirmDeleteDialog
          key={risk.id}
          isOpen={isDeleteDialogOpen}
          onDelete={() => handleDelete(risk.id)}
          onClose={() => handleClose()}
          headerText={t("Dpia.RiskMenu.DeleteConfirmHeader")}
          message={t("Dpia.RiskMenu.DeleteConfirmMessage")}
          confirmButtonLabel={t("Dpia.RiskMenu.DeleteConfirmButton")}
          errorMessage={deleteRiskAssessment.isError ? t("Dpia.RiskMenu.DeleteError") : undefined}
          id="confirm-delete"
          /** See more: https://stackoverflow.com/a/56285545 */
          onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
        />
      )}
    </>
  );
};

type CreateRiskAssessmentDialogProps = {
  onClose: () => void;
  dpiaId: string;
};

const CreateRiskAssessmentDialog: FC<CreateRiskAssessmentDialogProps> = ({ onClose, dpiaId }) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setFocus } = useForm<DpiaRiskAssessmentPayload>({});
  const createRiskMutation = useMutation(createDpiaRiskAssessmentMutationOptions());

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleCreate = (payload: Pick<DpiaRiskAssessmentPayload, "name" | "description">) => {
    createRiskMutation.mutate({ dpiaId, ...payload }, { onSuccess: onClose });
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("Dpia.RiskDialog.Title.Add")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleCreate)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={createRiskMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              id="name"
              rules={{ required: t("Input.Validation.RequiredName") }}
              label={t("Dpia.RiskDialog.NameLabel")}
              placeholder={t("Dpia.RiskDialog.NamePlaceholder")}
            />
            <FormDialog.TextField
              control={control}
              name="description"
              id="description"
              rules={{ required: t("Dpia.RiskDialog.DescriptionError") }}
              label={t("Dpia.RiskDialog.DescriptionLabel")}
              placeholder={t("Dpia.RiskDialog.DescriptionPlaceholder")}
              multiline
              rows={4}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={createRiskMutation.isPending}>
            {t("Dpia.RiskDialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

const RisksEmptyState: FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: theme => theme.palette.divider,
        borderRadius: 1,
        width: "100%",
        height: "450px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
      }}
    >
      <Stack direction="column" sx={{ alignItems: "center", justifyContent: "center", gap: 2 }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: "center",
            lineHeight: "30px",
            fontSize: theme => theme.typography.pxToRem(14),
            fontWeight: "400",
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {t("Dpia.Risks.EmptyState")}
        </Typography>
      </Stack>
    </Box>
  );
};

const RISK_CARD_BREAKPOINT = 600;
const SMALLER_THAN_RISK_CARD_BREAKPOINT_QUERY = `@container (max-width: ${RISK_CARD_BREAKPOINT}px)`;
const RISK_CARD_PADDING_BOTTOM = 4;
const RISK_CARD_PADDING_X = 3;
const RISK_CARD_PADDING_TOP = 3;
const RiskCard: FC<{
  risk: DpiaRiskAssessmentsResponse["riskAssessments"][0];
  form: DpiaRiskAssessmentsResponse;
  dpiaId: string;
  isReadOnly: boolean;
}> = ({ risk, form, isReadOnly, dpiaId }) => {
  const { t } = useTranslation();
  return (
    <Box
      component="section"
      aria-label={`${t("Dpia.Risks.RiskCard.Title")} ${risk.name}`}
      sx={{
        border: "1px solid",
        borderColor: theme => theme.palette.grey["300"],
        borderRadius: 1,
        containerType: "inline-size",
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: 0, right: 0, p: 1 }}>
        {!isReadOnly && <RiskAssessmentCardMenu riskAssessment={risk} />}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "5fr 1px 6fr",
          [SMALLER_THAN_RISK_CARD_BREAKPOINT_QUERY]: { gridTemplateColumns: "1fr", gap: 2 },
        }}
      >
        <Box
          sx={{
            px: RISK_CARD_PADDING_X,
            pt: RISK_CARD_PADDING_TOP,
            pb: RISK_CARD_PADDING_BOTTOM,
            height: "100%",
            [SMALLER_THAN_RISK_CARD_BREAKPOINT_QUERY]: { pb: 0 },
          }}
        >
          <RiskTitleAndDescription dpiaId={dpiaId} risk={risk} isReadOnly={isReadOnly} />
        </Box>
        <Divider
          orientation="vertical"
          sx={{ height: "100%", [SMALLER_THAN_RISK_CARD_BREAKPOINT_QUERY]: { display: "none" } }}
        />
        <Box sx={{ height: "100%" }}>
          <Box
            sx={{
              pb: RISK_CARD_PADDING_BOTTOM,
              pt: RISK_CARD_PADDING_TOP,
              px: RISK_CARD_PADDING_X,
              mr: 3,
              [SMALLER_THAN_RISK_CARD_BREAKPOINT_QUERY]: { pt: 0, px: RISK_CARD_PADDING_X + 2 },
            }}
          >
            {form.questionCards.map(question => (
              <DynamicForm.InlineFieldPrimary
                key={question.id}
                htmlFor={question.id}
                label={question.header}
                input={<DynamicForm.Input variant="inline" questionCard={question} />}
                infoPopoverContent={{
                  title: question.questionText,
                  body: question.helpInformation,
                  info: question.extraHelpInformation,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const RiskLevelLabel: FC<{ label: string; color: string; backgroundColor: string }> = ({
  label,
  color,
  backgroundColor,
}) => {
  return (
    <Box
      sx={{
        width: "fit-content",
        display: "flex",
        bgcolor: backgroundColor,
        alignItems: "center",
        justifyContent: "center",
        height: "18px",
        py: 0.5,
        px: 2,
        borderRadius: "8px",
      }}
    >
      <Typography color={color} sx={{ fontSize: theme => theme.typography.pxToRem(12), fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
};

const RiskMatrix: FC<{
  dpiaId: string;
}> = ({ dpiaId }) => {
  const riskAssessmentMatrixQuery = useQuery(dpiaRiskAssessmentMatrixByIdQueryOptions(dpiaId));
  if (!riskAssessmentMatrixQuery.data) return <CircularProgress />;
  const { matrix } = riskAssessmentMatrixQuery.data;

  const MATRIX_SIZE_HEIGHT = 200;
  /* Using the golden ratio to determine the width based on a fixed height */
  const goldenRatio = 1.61803398875;
  // Calculate the width using the golden ratio
  const MATRIX_SIZE_WIDTH = MATRIX_SIZE_HEIGHT * goldenRatio;
  // Calculate cell sizes
  const cellSizeHeight = Math.floor((MATRIX_SIZE_HEIGHT - (matrix.size - 1) * 4) / matrix.size);
  const cellSizeWidth = Math.floor((MATRIX_SIZE_WIDTH - (matrix.size - 1) * 4) / matrix.size);

  return (
    <Stack
      direction="row"
      sx={{ gap: 0.5, justifyContent: "flex-end", width: `${MATRIX_SIZE_WIDTH + 100}px`, position: "relative" }}
    >
      {/* Y-axis */}
      <Box
        sx={{
          height: MATRIX_SIZE_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "45px",
        }}
      >
        <Typography
          sx={{
            transform: "rotate(-90deg)",
            fontSize: "14px",
            fontWeight: 600,
            color: theme => theme.palette.text.primary,
            whiteSpace: "nowrap",
          }}
        >
          {matrix.yAxis.legendLabel}
        </Typography>
      </Box>
      <Stack sx={{ alignItems: "center", width: 24 }}>
        {[...matrix.yAxis.degrees].reverse().map((label, i) => (
          <Tooltip key={label} title={label} followCursor>
            <Typography
              sx={{
                height: cellSizeHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "3px",
                cursor: "pointer",
                color: theme => theme.palette.text.secondary,
                fontSize: "12px",
              }}
            >
              {matrix.size - i}
            </Typography>
          </Tooltip>
        ))}
      </Stack>
      <Stack sx={{ gap: 0.5 }}>
        {/* Grid Matrix */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${matrix.size}, ${cellSizeWidth}px)`,
            gridTemplateRows: `repeat(${matrix.size}, ${cellSizeHeight}px)`,
            gap: "3px",
            width: `${MATRIX_SIZE_WIDTH}px`,
          }}
        >
          {matrix.rows.map(row =>
            row.boxes.map(box => (
              <Box
                key={box.key}
                sx={{
                  "backgroundColor": box.backgroundColor,
                  "width": `${cellSizeWidth}px`,
                  "height": `${cellSizeHeight}px`,
                  "display": "flex",
                  "alignItems": "center",
                  "justifyContent": "center",
                  "position": "relative",
                  "&:hover .risk-titles": { display: "block" },
                }}
              >
                {box.value !== 0 && (
                  <Box
                    sx={{
                      border: theme => `1px solid ${theme.palette.grey["50"]}`,
                      bgcolor: theme => theme.palette.grey["50"],
                      borderRadius: "50%",
                      height: "24px",
                      width: "24px",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Typography
                      sx={theme => ({
                        fontSize: theme.typography.pxToRem(12),
                        fontWeight: 700,
                        color: theme.palette.text.tertiary,
                      })}
                    >
                      {box.value}
                    </Typography>
                  </Box>
                )}

                <Box
                  className="risk-titles"
                  sx={{
                    display: "none",
                    position: "absolute",
                    top: "100%",
                    left: "-95%",
                    right: 0,
                    transform: "translateX(-50%)",
                    bgcolor: theme => theme.palette.background.paper,
                    boxShadow: theme => theme.shadows[2],
                    borderRadius: "4px",
                    zIndex: "+1",
                    width: "300px",
                  }}
                >
                  <Stack sx={{ minHeight: "20px", maxHeight: "250px", overflowY: "auto" }}>
                    <Box
                      sx={{
                        position: "sticky",
                        top: 0,
                        bgcolor: "white",
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: theme => theme.palette.grey["200"],
                        fontWeight: "medium",
                      }}
                    >
                      <Box sx={{ padding: 2 }}>
                        <Stack direction="row" sx={{ justifyContent: "space-between", gap: 1 }}>
                          <Stack direction="column" sx={{ gap: 1 }}>
                            <RiskLevelLabel
                              label={box.tooltip}
                              backgroundColor={box.backgroundColor}
                              color={box.color}
                            />
                            {box.riskTitles.length > 0 && (
                              <Typography
                                sx={{
                                  paddingLeft: 1,
                                  fontSize: theme => theme.typography.pxToRem(14),
                                  fontWeight: 600,
                                }}
                              >
                                {box.riskTitles.length} {box.riskTitles.length === 1 ? "risk" : "risks"}
                              </Typography>
                            )}
                          </Stack>
                          <Stack direction="column">
                            <Stack direction="row" sx={{ justifyContent: "space-between", gap: 1 }}>
                              <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), fontWeight: 300 }}>
                                {matrix.yAxis.legendLabel}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  textAlign: "right",
                                  fontSize: "12px",
                                  fontWeight: 700,
                                  color: theme => theme.palette.text.secondary,
                                }}
                              >
                                {box.yAxisValue}
                              </Box>
                            </Stack>
                            <Stack direction="row" sx={{ justifyContent: "space-between", gap: 1 }}>
                              <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), fontWeight: 300 }}>
                                {matrix.xAxis.legendLabel}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  textAlign: "right",
                                  fontSize: "12px",
                                  fontWeight: 700,
                                  color: theme => theme.palette.text.secondary,
                                }}
                              >
                                {box.xAxisValue}
                              </Box>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>
                    </Box>
                    <Stack sx={{ maxHeight: "150px", overflowY: "auto" }}>
                      {box.riskTitles.map((title, i) => (
                        <Stack key={i} sx={{ gap: 0.5, paddingY: 1 }}>
                          <Stack direction="row" sx={{ alignItems: "center", paddingX: 2, gap: 1 }}>
                            <RiskIcon1 size="sm" />
                            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: 600 }}>
                              {title}
                            </Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* X-axis */}
        <Stack sx={{ gap: 0.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${matrix.size}, ${cellSizeWidth}px)`, gap: "3px" }}>
            {matrix.xAxis.degrees.map((label, i) => (
              <Tooltip key={label} title={label} followCursor>
                <Typography
                  sx={{
                    width: cellSizeWidth,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: theme => theme.palette.text.secondary,
                    fontSize: theme => theme.typography.pxToRem(12),
                  }}
                >
                  {i + 1}
                </Typography>
              </Tooltip>
            ))}
          </Box>
          <Typography
            sx={{
              width: "100%",
              textAlign: "center",
              fontSize: theme => theme.typography.pxToRem(14),
              fontWeight: 600,
              color: theme => theme.palette.text.primary,
            }}
          >
            {matrix.xAxis.legendLabel}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
